/**
 * Types pour la configuration des modules
 * Partagés entre BackOffice et les services
 */

/**
 * IDs des groupes de modules (correspond aux groupes de navigation du Sidebar)
 */
export type ModuleGroupId =
  | 'mon-espace'
  | 'ventes'
  | 'comptabilite'
  | 'catalogue'
  | 'contacts'
  | 'stock'
  | 'support'
  | 'administration';

/**
 * IDs des items de modules (correspond aux items individuels du Sidebar)
 */
export type ModuleItemId =
  // mon-espace
  | 'my-salary'
  // ventes
  | 'dashboard'
  | 'orders'
  | 'invoices'
  | 'cash-register'
  | 'sales-reports'
  | 'analytics'
  // comptabilite
  | 'accounting'
  | 'employee-delays'
  | 'salary-deductions'
  | 'payroll'
  // catalogue
  | 'articles'
  | 'categories'
  | 'reviews'
  // contacts
  | 'contacts'
  // stock
  | 'stocks'
  | 'stock-arrivals'
  | 'inventory'
  // support
  | 'faq'
  | 'faq-management'
  // administration
  | 'users'
  | 'sites'
  | 'enterprise'
  | 'history'
  | 'profile';

/**
 * Configuration des modules pour une entreprise
 */
export interface EnterpriseModulesConfig {
  groups: Record<ModuleGroupId, boolean>;
  items: Record<ModuleItemId, boolean>;
}

/**
 * Configuration par défaut, utilisée pour COMPLÉTER une config reçue de l'API (les clés
 * absentes prennent la valeur ci-dessous) et comme repli quand l'appel échoue.
 *
 * CONVENTION (à respecter impérativement, identique au backend
 * `backend/src/types/enterprise.types.ts`) : les clés ci-dessous sont GELÉES à `true` —
 * elles correspondent à l'état réellement observé en production.
 * **Toute clé ajoutée à l'avenir doit l'être à `false`** (opt-in explicite, activation par
 * tenant depuis la page Super Admin). Sinon, le remplissage étant permissif, un nouveau
 * module s'activerait automatiquement chez TOUS les tenants.
 */
export const DEFAULT_MODULES_CONFIG: EnterpriseModulesConfig = {
  groups: {
    'mon-espace': true,
    'ventes': true,
    'comptabilite': true,
    'catalogue': true,
    'contacts': true,
    'stock': true,
    'support': true,
    'administration': true,
  },
  items: {
    'my-salary': true,
    'dashboard': true,
    'orders': true,
    'invoices': true,
    'cash-register': true,
    'sales-reports': true,
    'analytics': true,
    'accounting': true,
    'employee-delays': true,
    'salary-deductions': true,
    'payroll': true,
    'articles': true,
    'categories': true,
    'reviews': true,
    'contacts': true,
    'stocks': true,
    'stock-arrivals': true,
    'inventory': true,
    'faq': true,
    'faq-management': true,
    'users': true,
    'sites': true,
    'enterprise': true,
    'history': true,
    'profile': true,
  },
};

/**
 * Tous les modules activés, quel que soit le contenu de `DEFAULT_MODULES_CONFIG`.
 * Réservé au `super_admin`, qui doit voir l'intégralité de l'application.
 *
 * Dérivé programmatiquement des clés du défaut (surtout PAS une seconde liste écrite à la
 * main : elle divergerait au premier ajout de module).
 */
const allTrue = <K extends string>(source: Record<K, boolean>): Record<K, boolean> =>
  Object.fromEntries(Object.keys(source).map((key) => [key, true])) as Record<K, boolean>;

export const ALL_MODULES_ENABLED: EnterpriseModulesConfig = {
  groups: allTrue(DEFAULT_MODULES_CONFIG.groups),
  items: allTrue(DEFAULT_MODULES_CONFIG.items),
};

/**
 * Complète une configuration de modules reçue de l'API avec les valeurs par défaut.
 *
 * Source unique de la sémantique côté client (Sidebar + ChatWidget). Le merge est conservé
 * même si le serveur renvoie désormais un objet complet : il protège la fenêtre de
 * déploiement (le bundle S3/CloudFront peut être en avance ou en retard sur la Lambda) et
 * la divergence de clés entre backend et front.
 *
 * `null` / `undefined` / `{}` => configuration par défaut.
 */
export function mergeModulesConfig(
  res?: Partial<EnterpriseModulesConfig> | null
): EnterpriseModulesConfig {
  if (!res || (!res.groups && !res.items)) return DEFAULT_MODULES_CONFIG;

  return {
    groups: { ...DEFAULT_MODULES_CONFIG.groups, ...res.groups },
    items: { ...DEFAULT_MODULES_CONFIG.items, ...res.items },
  };
}

/**
 * Mapping des routes vers les IDs de modules
 */
export const ROUTE_TO_MODULE: Record<string, ModuleItemId> = {
  '/my-salary': 'my-salary',
  '/dashboard': 'dashboard',
  '/orders': 'orders',
  '/invoices': 'invoices',
  '/cash-register': 'cash-register',
  '/sales-reports': 'sales-reports',
  '/analytics': 'analytics',
  '/accounting': 'accounting',
  '/employee-delays': 'employee-delays',
  '/salary-deductions': 'salary-deductions',
  '/payroll': 'payroll',
  '/articles': 'articles',
  '/articles/categories': 'categories',
  '/reviews': 'reviews',
  '/contacts': 'contacts',
  '/stocks': 'stocks',
  '/stock-arrivals': 'stock-arrivals',
  '/inventory': 'inventory',
  '/faq': 'faq',
  '/faq/management': 'faq-management',
  '/users': 'users',
  '/config/sites': 'sites',
  '/config/enterprise': 'enterprise',
  '/history': 'history',
  '/profile': 'profile',
};

/**
 * Mapping des routes vers les IDs de groupes
 */
export const ROUTE_TO_GROUP: Record<string, ModuleGroupId> = {
  '/my-salary': 'mon-espace',
  '/dashboard': 'ventes',
  '/orders': 'ventes',
  '/invoices': 'ventes',
  '/cash-register': 'ventes',
  '/sales-reports': 'ventes',
  '/analytics': 'ventes',
  '/accounting': 'comptabilite',
  '/employee-delays': 'comptabilite',
  '/salary-deductions': 'comptabilite',
  '/payroll': 'comptabilite',
  '/articles': 'catalogue',
  '/articles/categories': 'catalogue',
  '/reviews': 'catalogue',
  '/contacts': 'contacts',
  '/stocks': 'stock',
  '/stock-arrivals': 'stock',
  '/inventory': 'stock',
  '/faq': 'support',
  '/faq/management': 'support',
  '/users': 'administration',
  '/config/sites': 'administration',
  '/config/enterprise': 'administration',
  '/history': 'administration',
  '/profile': 'administration',
};
